import { FocusEvent, KeyboardEventHandler, ReactNode, Ref, useCallback } from 'react';
import { Add, Remove } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, TextField } from '@mui/material';
import { parseQtyInput, qtyStep, qtyStepAttr, roundQty } from '../util/quantity';
import { selectOnFocus } from '../util/focusField';

type Props = {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  size?: 'small' | 'medium';
  label?: string;
  error?: boolean;
  helperText?: ReactNode;
  id?: string;
  inputRef?: Ref<HTMLInputElement>;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  compact?: boolean;
  fullWidth?: boolean;
  required?: boolean;
  /** When true, the stepper accepts decimal quantities (item.is_fractional). */
  fractional?: boolean;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const hideNativeStepperSx = {
  MozAppearance: 'textfield',
  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
} as const;

const stepperButtonSx = {
  position: 'absolute',
  zIndex: 1,
  opacity: 0,
  width: 22,
  height: 22,
  p: 0,
  transition: 'opacity 150ms ease',
  bgcolor: 'background.paper',
  boxShadow: 1,
  '.qty-stepper-root:hover &, .qty-stepper-root:focus-within &, &:focus-visible': {
    opacity: 1,
  },
  '&:hover': {
    opacity: 1,
  },
} as const;

const QuantityStepperInput = ({
  value,
  onChange,
  min = 1,
  max = Number.MAX_SAFE_INTEGER,
  disabled = false,
  size = 'small',
  label,
  error = false,
  helperText,
  id,
  inputRef,
  onFocus,
  onKeyDown,
  compact = false,
  fullWidth = true,
  required = false,
  fractional = false,
}: Props) => {
  const { t } = useTranslation();
  const step = qtyStep(fractional);
  const stepAttr = qtyStepAttr(fractional);
  const applyDelta = useCallback((delta: number) => {
    if (disabled) return;
    onChange(clamp(roundQty(value + delta), min, max));
  }, [disabled, max, min, onChange, value]);

  const buttonInset = compact ? 2 : 6;
  const buttonBottom = compact ? 5 : 10;

  return (
    <Box
      className="qty-stepper-root"
      sx={{
        position: 'relative',
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      <TextField
        id={id}
        label={compact ? undefined : label}
        value={value}
        type="number"
        variant="filled"
        size={size}
        fullWidth={fullWidth}
        required={required}
        disabled={disabled}
        error={error}
        helperText={compact ? undefined : helperText}
        margin={compact ? 'none' : undefined}
        inputRef={inputRef}
        onFocus={(event) => {
          // Keyboard-first entry: typing replaces the quantity.
          selectOnFocus(event);
          onFocus?.(event as FocusEvent<HTMLInputElement>);
        }}
        onKeyDown={onKeyDown}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => {
          const parsed = parseQtyInput(event.target.value, fractional);
          if (parsed == null) return;
          onChange(clamp(parsed, min, max));
        }}
        slotProps={{
          htmlInput: {
            min,
            max,
            step: stepAttr,
            inputMode: fractional ? 'decimal' : 'numeric',
            autoComplete: 'off',
          },
          input: {
            sx: {
              '& input': {
                ...hideNativeStepperSx,
                textAlign: 'center',
                fontWeight: 600,
              },
            },
          },
        }}
        sx={compact ? {
          '& .MuiFilledInput-root': {
            borderRadius: 0,
            bgcolor: 'transparent',
            '&::before, &::after': {
              display: 'none',
            },
          },
          '& .MuiFilledInput-input': {
            py: 0.75,
            px: 3,
          },
        } : {
          '& .MuiFilledInput-input': {
            px: 3,
          },
        }}
      />

      {!disabled && (
        <>
          <IconButton
            size="small"
            tabIndex={-1}
            aria-label={t('common:ui.aria.decreaseQuantity')}
            disabled={value <= min}
            onClick={(event) => {
              event.stopPropagation();
              applyDelta(-step);
            }}
            sx={{
              ...stepperButtonSx,
              left: buttonInset,
              bottom: buttonBottom,
            }}
          >
            <Remove sx={{ fontSize: 16 }} />
          </IconButton>

          <IconButton
            size="small"
            tabIndex={-1}
            aria-label={t('common:ui.aria.increaseQuantity')}
            disabled={value >= max}
            onClick={(event) => {
              event.stopPropagation();
              applyDelta(+step);
            }}
            sx={{
              ...stepperButtonSx,
              right: buttonInset,
              bottom: buttonBottom,
            }}
          >
            <Add sx={{ fontSize: 16 }} />
          </IconButton>
        </>
      )}
    </Box>
  );
};

export default QuantityStepperInput;
